import { useState, useEffect } from 'react';
import EditTagsModal from './EditTagsModal';
import { extractString, extractStringArray } from '../../utils/sanitizeData';

interface InvoiceHeroCardProps {
  publicToken?: string;
  vendorName?: unknown;
  invoiceDate?: unknown;
  dueDate?: unknown;
  totalAmount?: unknown;
  currency?: unknown;
  initialTags?: unknown[];
  onTagsChange?: (tags: string[]) => void;
}

export default function InvoiceHeroCard({
  publicToken,
  vendorName = 'Unknown Vendor',
  invoiceDate = '—',
  dueDate = '—',
  totalAmount = '0.00',
  currency,
  initialTags = [],
  onTagsChange,
}: InvoiceHeroCardProps) {
  const safeVendor = extractString(vendorName, 'Unknown Vendor');
  const safeDate = extractString(invoiceDate, '—');
  const safeDueDate = extractString(dueDate, '—');
  const safeTotal = extractString(totalAmount, '0.00');
  const safeCurrency = extractString(currency, '');
  const safeTags = extractStringArray(initialTags);

  const [tags, setTags] = useState<string[]>(safeTags);
  const [isEditTagsModalOpen, setIsEditTagsModalOpen] = useState(false);

  useEffect(() => {
    setTags(extractStringArray(initialTags));
  }, [initialTags]);

  const handleSaveTags = (newTags: string[]) => {
    setTags(newTags);
    onTagsChange?.(newTags);
  };

  const formattedTotal = () => {
    if (safeCurrency === 'EUR' || safeCurrency === '€') return `€${safeTotal}`;
    if (safeCurrency === 'USD' || safeCurrency === '$') return `$${safeTotal}`;
    if (safeCurrency && safeCurrency !== 'UNKNOWN') return `${safeTotal} ${safeCurrency}`;
    return safeTotal;
  };

  return (
    <>
      <div className="relative overflow-hidden rounded-2.5xl hero-gradient p-6 sm:p-7 text-white shadow-hero mb-6">
        {/* Subtle Glow Overlay */}
        <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-brand-light/10 blur-3xl pointer-events-none" />
        <div className="absolute -left-16 -bottom-16 w-64 h-64 rounded-full bg-brand-green/10 blur-3xl pointer-events-none" />

        <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-center">
          {/* Stat 1: Vendor */}
          <div>
            <div className="text-[11px] font-mono font-medium text-brand-light uppercase tracking-wider mb-1">
              Vendor / Issuer
            </div>
            <div className="text-xl sm:text-2xl font-bold text-white tracking-tight flex items-center gap-2">
              <span>{safeVendor}</span>
              <svg className="w-5 h-5 text-brand-green fill-current" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="text-xs text-white/60 font-mono mt-0.5">
              Verified Issuer
            </div>
          </div>

          {/* Stat 2: Date & Due */}
          <div>
            <div className="text-[11px] font-mono font-medium text-brand-light uppercase tracking-wider mb-1">
              Issue & Due Date
            </div>
            <div className="text-base font-mono font-semibold text-white">
              {safeDate}
            </div>
            <div className="text-xs font-mono text-white/60 mt-0.5">
              Due: {safeDueDate}
            </div>
          </div>

          {/* Stat 3: Total Amount */}
          <div>
            <div className="text-[11px] font-mono font-medium text-brand-light uppercase tracking-wider mb-1">
              Total Amount
            </div>
            <div className="text-2xl sm:text-3xl font-mono font-bold text-brand-green tracking-tight">
              {formattedTotal()}
            </div>
            <div className="text-xs font-mono text-white/60 mt-0.5">
              Parsed from document
            </div>
          </div>

          {/* Stat 4: Tags */}
          <div>
            <div className="flex items-center justify-between text-[11px] font-mono font-medium text-brand-light uppercase tracking-wider mb-2">
              <span>Classification Tags</span>
              <button
                type="button"
                onClick={() => setIsEditTagsModalOpen(true)}
                className="text-[10px] text-brand-green hover:underline cursor-pointer lowercase"
              >
                edit
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {tags.length > 0 ? (
                tags.map((tag, idx) => (
                  <span
                    key={idx}
                    className="px-2.5 py-1 rounded-lg text-xs font-mono font-medium bg-white/10 text-white backdrop-blur-sm border border-white/15"
                  >
                    #{tag}
                  </span>
                ))
              ) : (
                <span className="text-xs font-mono text-white/40 italic">
                  No tags
                </span>
              )}

              <button
                type="button"
                onClick={() => setIsEditTagsModalOpen(true)}
                className="px-2.5 py-1 rounded-lg text-xs font-mono font-semibold bg-brand-vib/70 text-white hover:bg-brand-vib transition-colors border border-white/20 shadow-xs inline-flex items-center gap-1 cursor-pointer"
                title="Edit and Add Tags"
              >
                <span>+ Tag</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Tags Modal */}
      <EditTagsModal
        isOpen={isEditTagsModalOpen}
        onClose={() => setIsEditTagsModalOpen(false)}
        publicToken={publicToken}
        initialTags={tags}
        onSaveTags={handleSaveTags}
      />
    </>
  );
}
